import React, { useState, useEffect, useCallback } from 'react';
import { Mail, AlertTriangle, CheckCircle, Loader2, LogOut, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import axiosInstance from '../../axiosConfig';
import './VerificationRequired.css';

const VerificationRequired = () => {
  const { logout } = useSecureAuth();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const fetchVerificationStatus = useCallback(async () => {
    try {
      setError(null);
      const response = await axiosInstance.get('/merchant/auth/verification-status');

      if (response.data.success) {
        setVerificationStatus(response.data);

        // If verified or not blocked, send to /overview (canonical landing)
        if (response.data.isEmailVerified || !response.data.isBlocked) {
          navigate('/overview', { replace: true });
          return;
        }
      }
    } catch (err) {
      console.error('Error fetching verification status:', err);
      // If 401, user is not authenticated - redirect to login
      if (err.response?.status === 401) {
        navigate('/login', { replace: true });
        return;
      }
      setError('Unable to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchVerificationStatus();
  }, [fetchVerificationStatus]);

  const handleResendVerification = async () => {
    if (sending || sent) return;

    try {
      setSending(true);
      setError(null);
      await axiosInstance.post('/merchant/auth/resend-verification');
      setSent(true);

      // Reset sent state after 60 seconds
      setTimeout(() => setSent(false), 60000);
    } catch (err) {
      console.error('Error resending verification email:', err);
      setError(err.response?.data?.error || 'Failed to send verification email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCheckStatus = () => {
    setLoading(true);
    fetchVerificationStatus();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="vr-container">
        <div className="vr-card">
          <Loader2 size={40} className="vr-spinner" />
          <p>Checking verification status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vr-container">
      <div className="vr-card">
        <div className="vr-icon-wrapper">
          <AlertTriangle size={48} className="vr-icon" />
        </div>

        <h1 className="vr-title">Email Verification Required</h1>

        <p className="vr-message">
          Your account has been restricted because your email address has not been verified.
          Please verify your email to continue using the platform.
        </p>

        {verificationStatus?.email && (
          <div className="vr-email-box">
            <Mail size={20} />
            <span>{verificationStatus.email}</span>
          </div>
        )}

        {error && (
          <div className="vr-error-message">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="vr-actions">
          {sent ? (
            <div className="vr-sent-message">
              <CheckCircle size={18} />
              <span>Verification email sent! Please check your inbox.</span>
            </div>
          ) : (
            <button
              className="vr-btn vr-btn-primary"
              onClick={handleResendVerification}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="vr-spinner" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={18} />
                  Resend Verification Email
                </>
              )}
            </button>
          )}

          <button
            className="vr-btn vr-btn-secondary"
            onClick={handleCheckStatus}
          >
            <RefreshCw size={18} />
            I've Verified, Check Status
          </button>

          <button
            className="vr-btn vr-btn-ghost"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>

        <div className="vr-help">
          <p>
            Didn't receive the email? Check your spam folder or try resending.
          </p>
          <p>
            Need help? Contact support at{' '}
            <a href="mailto:support@userevstar.com">support@userevstar.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationRequired;
